// ModalAnnulationChantier : Modal pour l'annulation d'un chantier
// Permet de confirmer l'abandon et de comptabiliser les coûts engagés

import { useState, useEffect } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateChantier } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  AlertTriangle,
  XCircle,
  ChevronRight,
  ChevronLeft,
  Euro,
  FileText,
  Users,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ModalAnnulationChantierProps {
  isOpen: boolean;
  onClose: () => void;
  chantierId: string;
  chantierNom: string;
  // Infos pour l'affichage
  budgetHT?: number;
  dateDebut?: string;
  onConfirm: () => void;
}

type Step = 1 | 2 | 3;
type RaisonAnnulation = 'client_refuse' | 'probleme_technique' | 'budget' | 'autre';

interface FormData {
  raison: RaisonAnnulation;
  raison_detail: string;
  a_couts_engages: boolean;
  // Coûts à comptabiliser
  cout_main_oeuvre: number;
  cout_materiaux: number;
  cout_sous_traitance: number;
  cout_autres: number;
  notes_couts: string;
}

const RAISONS_ANNULATION: Record<RaisonAnnulation, { label: string; description: string }> = {
  client_refuse: { label: 'Client a refusé', description: 'Le client a annulé le projet' },
  probleme_technique: { label: 'Problème technique', description: 'Impossibilité technique de réaliser le chantier' },
  budget: { label: 'Problème de budget', description: 'Budget insuffisant ou désaccord financier' },
  autre: { label: 'Autre raison', description: 'Autre motif d\'annulation' },
};

export function ModalAnnulationChantier({
  isOpen,
  onClose,
  chantierId,
  chantierNom,
  budgetHT = 0,
  dateDebut,
  onConfirm,
}: ModalAnnulationChantierProps) {
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();
  useModalProtection(isOpen);
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    raison: 'client_refuse',
    raison_detail: '',
    a_couts_engages: false,
    cout_main_oeuvre: 0,
    cout_materiaux: 0,
    cout_sous_traitance: 0,
    cout_autres: 0,
    notes_couts: '',
  });

  // Stats du chantier
  const [stats, setStats] = useState<{
    nbAffectations: number;
    nbFrais: number;
    totalFrais: number;
  }>({
    nbAffectations: 0,
    nbFrais: 0,
    totalFrais: 0,
  });

  // Charger les stats au montage
  useEffect(() => {
    if (isOpen && chantierId) {
      loadStats();
      setStep(1);
      setFormData({
        raison: 'client_refuse',
        raison_detail: '',
        a_couts_engages: false,
        cout_main_oeuvre: 0,
        cout_materiaux: 0,
        cout_sous_traitance: 0,
        cout_autres: 0,
        notes_couts: '',
      });
    }
  }, [isOpen, chantierId]);

  const loadStats = async () => {
    try {
      // Compter les affectations
      const { count: nbAffectations } = await supabase
        .from('affectations_chantiers')
        .select('*', { count: 'exact', head: true })
        .eq('chantier_id', chantierId);

      // Compter et totaliser les frais
      const { data: fraisData, count: nbFrais } = await supabase
        .from('frais_chantier')
        .select('montant_ht', { count: 'exact' })
        .eq('chantier_id', chantierId);

      const totalFrais = fraisData?.reduce((sum, f) => sum + (f.montant_ht || 0), 0) || 0;

      setStats({
        nbAffectations: nbAffectations || 0,
        nbFrais: nbFrais || 0,
        totalFrais,
      });
    } catch (error) {
      console.error('[ModalAnnulationChantier] Erreur chargement stats:', error);
    }
  };

  // Calculer le total des coûts à ajouter
  const totalCoutsAAjouter =
    formData.cout_main_oeuvre +
    formData.cout_materiaux +
    formData.cout_sous_traitance +
    formData.cout_autres;

  // Confirmer l'annulation
  const handleConfirm = async () => {
    setLoading(true);
    try {
      // 1. Si coûts engagés, les enregistrer comme frais
      if (formData.a_couts_engages && totalCoutsAAjouter > 0) {
        const fraisACreer = [];

        if (formData.cout_main_oeuvre > 0) {
          fraisACreer.push({
            chantier_id: chantierId,
            type_frais: 'Main d\'œuvre (annulation)',
            montant_ht: formData.cout_main_oeuvre,
            date_frais: format(new Date(), 'yyyy-MM-dd'),
            description: `Coûts engagés avant annulation - ${formData.notes_couts || 'Main d\'œuvre'}`,
          });
        }
        if (formData.cout_materiaux > 0) {
          fraisACreer.push({
            chantier_id: chantierId,
            type_frais: 'Matériaux (annulation)',
            montant_ht: formData.cout_materiaux,
            date_frais: format(new Date(), 'yyyy-MM-dd'),
            description: `Coûts engagés avant annulation - Matériaux achetés`,
          });
        }
        if (formData.cout_sous_traitance > 0) {
          fraisACreer.push({
            chantier_id: chantierId,
            type_frais: 'Sous-traitance (annulation)',
            montant_ht: formData.cout_sous_traitance,
            date_frais: format(new Date(), 'yyyy-MM-dd'),
            description: `Coûts engagés avant annulation - Sous-traitance`,
          });
        }
        if (formData.cout_autres > 0) {
          fraisACreer.push({
            chantier_id: chantierId,
            type_frais: 'Autres frais (annulation)',
            montant_ht: formData.cout_autres,
            date_frais: format(new Date(), 'yyyy-MM-dd'),
            description: `Coûts engagés avant annulation - ${formData.notes_couts || 'Autres'}`,
          });
        }

        // Récupérer l'entreprise_id du chantier
        const { data: chantierData } = await supabase
          .from('chantiers')
          .select('entreprise_id')
          .eq('id', chantierId)
          .single();

        if (chantierData?.entreprise_id) {
          for (const frais of fraisACreer) {
            const { error } = await supabase.from('frais_chantier').insert({
              ...frais,
              entreprise_id: chantierData.entreprise_id,
            });
            if (error) {
              console.error('[ModalAnnulationChantier] Erreur création frais:', error);
            }
          }
        }
      }

      // 2. Supprimer les affectations futures
      const today = format(new Date(), 'yyyy-MM-dd');
      await supabase
        .from('affectations_chantiers')
        .delete()
        .eq('chantier_id', chantierId)
        .gt('date_debut', today);

      // 3. Libérer les membres
      const { data: membresAffectes } = await supabase
        .from('affectations_chantiers')
        .select('membre_equipe_id')
        .eq('chantier_id', chantierId);

      if (membresAffectes && membresAffectes.length > 0) {
        const membreIds = [...new Set(membresAffectes.map(m => m.membre_equipe_id).filter(Boolean))];
        if (membreIds.length > 0) {
          await supabase
            .from('membres_equipe')
            .update({ statut: 'disponible' })
            .in('id', membreIds)
            .eq('statut', 'sur_chantier');
        }
      }

      // 4. Mettre à jour le statut du chantier
      const notes_annulation = `Annulé le ${format(new Date(), 'dd/MM/yyyy')} - ${RAISONS_ANNULATION[formData.raison].label}${formData.raison_detail ? ` : ${formData.raison_detail}` : ''}`;

      const { error: updateError } = await supabase
        .from('chantiers')
        .update({
          etat_chantier: 'annule',
          // Stocker la raison dans les notes ou un champ dédié si existant
          // Pour l'instant on ne modifie que l'état
        })
        .eq('id', chantierId);

      if (updateError) {
        console.error('[ModalAnnulationChantier] Erreur mise à jour chantier:', updateError);
        toast.error('Erreur lors de l\'annulation du chantier');
        return;
      }

      // 5. Recalculer les métriques AVANT de notifier les autres composants
      if (import.meta.env.DEV) console.log('[ModalAnnulationChantier] Recalcul des métriques après annulation...');
      const { data: metricsData, error: metricsError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (metricsError) {
        console.error('[ModalAnnulationChantier] ❌ Erreur recalcul métriques:', metricsError);
      } else if (metricsData) {
        // Stocker les métriques recalculées pour le realtime
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierId,
            metrics: metricsData,
            updated_at: new Date().toISOString()
          });
        if (import.meta.env.DEV) console.log('[ModalAnnulationChantier] Métriques rafraîchies après annulation');
      }

      // 6. Notifier les composants non migrés
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));
      // Invalider le cache React Query
      if (entrepriseId) invalidateChantier(queryClient, entrepriseId, chantierId);

      toast.success(`Chantier "${chantierNom}" annulé avec succès`);
      onConfirm();
      onClose();
    } catch (error) {
      console.error('[ModalAnnulationChantier] Erreur:', error);
      toast.error('Erreur lors de l\'annulation');
    } finally {
      setLoading(false);
    }
  };

  // Rendu des étapes
  const renderStep1 = () => (
    <div className="space-y-4">
      {/* Avertissement */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
        <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-red-500">Attention : cette action est significative</p>
          <p className="text-sm text-muted-foreground mt-1">
            L'annulation d'un chantier libère les équipes affectées et archive le projet.
            Les coûts déjà engagés pourront être comptabilisés à l'étape suivante.
          </p>
        </div>
      </div>

      {/* Résumé du chantier */}
      <div className="p-4 rounded-lg bg-card border">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Résumé du chantier
        </h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Nom :</span>
            <span className="ml-2 font-medium">{chantierNom}</span>
          </div>
          {budgetHT > 0 && (
            <div>
              <span className="text-muted-foreground">Budget :</span>
              <span className="ml-2 font-medium">{budgetHT.toLocaleString('fr-FR')} € HT</span>
            </div>
          )}
          {dateDebut && (
            <div>
              <span className="text-muted-foreground">Date début :</span>
              <span className="ml-2">{format(new Date(dateDebut), 'dd/MM/yyyy', { locale: fr })}</span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Affectations :</span>
            <span className="ml-2">{stats.nbAffectations}</span>
          </div>
          {stats.totalFrais > 0 && (
            <div>
              <span className="text-muted-foreground">Frais enregistrés :</span>
              <span className="ml-2">{stats.totalFrais.toLocaleString('fr-FR')} € HT</span>
            </div>
          )}
        </div>
      </div>

      {/* Raison de l'annulation */}
      <div className="space-y-3">
        <Label>Raison de l'annulation</Label>
        <RadioGroup
          value={formData.raison}
          onValueChange={(value) => setFormData({ ...formData, raison: value as RaisonAnnulation })}
          className="space-y-2"
        >
          {(Object.keys(RAISONS_ANNULATION) as RaisonAnnulation[]).map((key) => (
            <div
              key={key}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                formData.raison === key
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
              onClick={() => setFormData({ ...formData, raison: key })}
            >
              <RadioGroupItem value={key} id={key} className="mt-0.5" />
              <div>
                <Label htmlFor={key} className="font-medium cursor-pointer">
                  {RAISONS_ANNULATION[key].label}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {RAISONS_ANNULATION[key].description}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Détails supplémentaires */}
      {formData.raison === 'autre' && (
        <div className="space-y-2">
          <Label htmlFor="raison_detail">Précisez la raison</Label>
          <Textarea
            id="raison_detail"
            value={formData.raison_detail}
            onChange={(e) => setFormData({ ...formData, raison_detail: e.target.value })}
            placeholder="Décrivez la raison de l'annulation..."
            rows={3}
          />
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Question principale */}
      <div className="p-4 rounded-lg bg-card border">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <Euro className="h-4 w-4" />
          Coûts non enregistrés ?
        </h4>
        <p className="text-sm text-muted-foreground mb-4">
          Avant de clôturer, vérifiez qu'il ne reste pas de coûts à ajouter :
        </p>

        <RadioGroup
          value={formData.a_couts_engages ? 'oui' : 'non'}
          onValueChange={(value) => setFormData({ ...formData, a_couts_engages: value === 'oui' })}
          className="space-y-2"
        >
          <div
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              !formData.a_couts_engages
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            )}
            onClick={() => setFormData({ ...formData, a_couts_engages: false })}
          >
            <RadioGroupItem value="non" id="non" />
            <Label htmlFor="non" className="cursor-pointer">
              Non, aucun coût supplémentaire à enregistrer
            </Label>
          </div>
          <div
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              formData.a_couts_engages
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            )}
            onClick={() => setFormData({ ...formData, a_couts_engages: true })}
          >
            <RadioGroupItem value="oui" id="oui" />
            <Label htmlFor="oui" className="cursor-pointer">
              Oui, des coûts ont été engagés
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Formulaire de coûts si nécessaire */}
      {formData.a_couts_engages && (
        <div className="space-y-4 p-4 rounded-lg bg-card border">
          <h4 className="font-medium">Détail des coûts engagés</h4>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cout_main_oeuvre" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Main d'œuvre
              </Label>
              <div className="relative">
                <Input
                  id="cout_main_oeuvre"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_main_oeuvre || ''}
                  onChange={(e) => setFormData({ ...formData, cout_main_oeuvre: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  € HT
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cout_materiaux">Matériaux achetés</Label>
              <div className="relative">
                <Input
                  id="cout_materiaux"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_materiaux || ''}
                  onChange={(e) => setFormData({ ...formData, cout_materiaux: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  € HT
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cout_sous_traitance">Sous-traitance</Label>
              <div className="relative">
                <Input
                  id="cout_sous_traitance"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_sous_traitance || ''}
                  onChange={(e) => setFormData({ ...formData, cout_sous_traitance: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  € HT
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cout_autres">Autres frais</Label>
              <div className="relative">
                <Input
                  id="cout_autres"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_autres || ''}
                  onChange={(e) => setFormData({ ...formData, cout_autres: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  € HT
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes_couts">Notes (optionnel)</Label>
            <Textarea
              id="notes_couts"
              value={formData.notes_couts}
              onChange={(e) => setFormData({ ...formData, notes_couts: e.target.value })}
              placeholder="Détails sur les coûts engagés..."
              rows={2}
            />
          </div>

          {/* Total */}
          {totalCoutsAAjouter > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <span className="font-medium">Total à comptabiliser :</span>
              <span className="text-lg font-bold text-amber-600">
                {totalCoutsAAjouter.toLocaleString('fr-FR')} € HT
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Confirmation finale */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
        <XCircle className="h-6 w-6 text-red-500 shrink-0" />
        <div>
          <p className="font-medium text-red-500">Confirmer l'annulation</p>
          <p className="text-sm text-muted-foreground mt-1">
            Cette action va :
          </p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
            <li>Passer le chantier en statut "Annulé"</li>
            <li>Libérer les {stats.nbAffectations} membre(s) affecté(s)</li>
            <li>Supprimer les affectations futures</li>
            {formData.a_couts_engages && totalCoutsAAjouter > 0 && (
              <li>Enregistrer {totalCoutsAAjouter.toLocaleString('fr-FR')} € HT de coûts engagés</li>
            )}
          </ul>
        </div>
      </div>

      {/* Récapitulatif */}
      <div className="p-4 rounded-lg bg-card border">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Récapitulatif
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Chantier :</span>
            <span className="font-medium">{chantierNom}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Raison :</span>
            <span>{RAISONS_ANNULATION[formData.raison].label}</span>
          </div>
          {formData.raison_detail && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Détails :</span>
              <span className="truncate max-w-[200px]">{formData.raison_detail}</span>
            </div>
          )}
          {formData.a_couts_engages && totalCoutsAAjouter > 0 && (
            <div className="flex justify-between text-amber-600">
              <span>Coûts à enregistrer :</span>
              <span className="font-medium">{totalCoutsAAjouter.toLocaleString('fr-FR')} € HT</span>
            </div>
          )}
        </div>
      </div>

      {/* Note importante */}
      <p className="text-xs text-muted-foreground text-center">
        Le chantier annulé n'entrera pas dans les statistiques de performance.
        Vous pourrez le réactiver plus tard si le client change d'avis.
      </p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Annuler le chantier
            <span className="text-sm font-normal text-muted-foreground ml-2">
              Étape {step}/3
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Indicateur d'étapes */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={cn(
                'flex-1 h-1.5 rounded-full transition-colors',
                s <= step ? 'bg-red-500' : 'bg-muted'
              )}
            />
          ))}
        </div>

        {/* Contenu de l'étape */}
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="ghost"
            onClick={() => (step === 1 ? onClose() : setStep((step - 1) as Step))}
            disabled={loading}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 1 ? 'Annuler' : 'Retour'}
          </Button>

          {step < 3 ? (
            <Button
              onClick={() => setStep((step + 1) as Step)}
              disabled={step === 1 && formData.raison === 'autre' && !formData.raison_detail}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Annulation...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Confirmer l'annulation
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalAnnulationChantier;
